import { createRoute, useLoaderData, useParams } from "@tanstack/react-router";
import { type QueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { Typography } from "~/common/mui";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { configuratorPageQueryOptions } from "~/app/api/pageData/configuratorPageQuery";
import { ConfiguratorHeader } from "~/app/shell/ConfiguratorHeader";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { LegacyConfiguratorPageEntry, TimelineConfiguratorPageEntry } from "~/pages/ConfiguratorPage/ConfiguratorPage";
import { findConfiguratorRegistration } from "~/bridges/configurator/configuratorRegistry";
import { openAuthOverlay } from "~/features/registration/components/redux/AuthActions";
import {
  configuratorConfigurationQueryOptions,
  type ConfiguratorDefinition,
  useConfiguratorViewModel,
} from "~/features/configurator-v2";

export async function loadConfiguratorPageData(
  context: { queryClient: QueryClient },
  productCode: string,
  search = "",
) {
  const registration = findConfiguratorRegistration(productCode);
  const configurationPromise =
    registration?.engine === "timeline"
      ? context.queryClient.ensureQueryData(configuratorConfigurationQueryOptions(registration.definition.productCode))
      : Promise.resolve();
  const [result] = await Promise.all([
    context.queryClient.ensureQueryData(configuratorPageQueryOptions(productCode, search)),
    configurationPromise,
  ]);

  return unwrapPageInitialData(result);
}

function TimelineConfiguratorHeader({ definition }: { definition: ConfiguratorDefinition }) {
  const viewModel = useConfiguratorViewModel(definition);

  return viewModel.configuration && viewModel.context ? (
    <ConfiguratorHeader
      configuration={viewModel.configuration}
      context={viewModel.context}
      complete={viewModel.complete}
      titleKey={definition.titleKey}
    />
  ) : null;
}

function ConfiguratorRoute() {
  const dispatch = useDispatch();
  const result = useLoaderData({ strict: false }) as PageInitialData<void>;
  const shellProps = useStorefrontShellProps();
  const params = useParams({ strict: false });
  const registration = findConfiguratorRegistration(params.productCode);

  // The configurator renders its own full-page UI (no CMS slot structure), so it is body-only.
  // Default the templateUid: the dedicated route knows its template, and the page-data metadata
  // can arrive as the withoutResolvedPage fallback (null templateUid) at runtime.
  const metadata = {
    ...result.metadata,
    templateUid: result.metadata.templateUid ?? "ProductConfigurationTemplate",
  };

  return (
    <StorefrontShellFrame
      variant={shellProps.variant}
      metadata={metadata}
      header={
        registration?.engine === "timeline" ? (
          <TimelineConfiguratorHeader definition={registration.definition} />
        ) : undefined
      }
    >
      {!registration ? (
        <Typography variant="bodyMedium">No configurator definition available!</Typography>
      ) : registration.engine === "timeline" ? (
        <TimelineConfiguratorPageEntry
          definition={registration.definition}
          onRegistrationRequested={() => dispatch(openAuthOverlay("register"))}
        />
      ) : (
        <LegacyConfiguratorPageEntry productCode={params.productCode} />
      )}
    </StorefrontShellFrame>
  );
}

const sharedRouteOptions = {
  getParentRoute: () => rootRoute,
  loader: async ({
    context,
    params,
    location,
  }: {
    context: { queryClient: QueryClient };
    params: { productCode: string };
    location: { pathname: string; searchStr: string };
  }) => loadConfiguratorPageData(context, params.productCode, location.searchStr),
  component: ConfiguratorRoute,
} as const;

export const configuratorRoute = createRoute({
  ...sharedRouteOptions,
  path: "/$productCode/configuratorPage/LinearConfigurator",
});

export const railsConfiguratorRoute = createRoute({
  ...sharedRouteOptions,
  path: "/rails/$productCode/railsConfiguratorPage/LinearConfigurator",
});

export const carriagesConfiguratorRoute = createRoute({
  ...sharedRouteOptions,
  path: "/carriages/$productCode/carriagesConfiguratorPage/LinearConfigurator",
});

export const shaftConfiguratorRoute = createRoute({
  ...sharedRouteOptions,
  path: "/shaft/$productCode/shaftConfiguratorPage/LinearConfigurator",
});
